/**
 * 形态条 —— 「你正在出:商品主图 / 爆点组图 / 全渠道套图」(2026-07-30)。
 *
 * ═══ 为什么必须有这一条 ═══════════════════════════════════════════════════════
 * 三张卡 `poster_single` / `poster_group` / `poster_multiratio` 的 **route 完全相同**
 * (都是 `/ai-haibao/create`),只差一个 `preset`。同一个 URL 三种行为、三种出图张数、
 * 三种段内容 —— 页面上必须有一处告诉用户他在哪一态,否则「我点的是组图,怎么只出一张」
 * 这类问题根本无从自查。
 *
 * ═══ 为什么只显示、不给页内切换 ═══════════════════════════════════════════════
 * 页内切换会承诺一件它兑现不了的事:**切了内容怎么办**。商品主图 → 爆点组图,已写的
 * 文案留不留?爆点要不要从文案里拆?`applyMode` 里那句「保留已填的爆点(用户来回切时
 * 别把内容弄丢)」就是这个承诺留下的补丁。
 *
 * 所以这里给的是 **handoff 而不是 mode switch**:带着文案去另一张卡,并且**明说**什么
 * 会保留、什么要重填。它只在用户**已经写了字**时出现 —— 没写字的人点面包屑回卡墙就行,
 * 没有丢失风险,也就不需要这个入口。
 */
import { useState } from "react";
import { ArrowRight } from "lucide-react";

import { useNavigate } from "react-router-dom";

/** 单帧创建页的三种形态(与 `SCENE_CARDS` 的 poster 三卡同集合)。 */
export type PosterMode = "single" | "group" | "ratioSet";

/** 换形态时**保留什么、要重填什么** —— 不假装无损。 */
const HANDOFF: Record<PosterMode, { to: PosterMode; label: string; keeps: string }[]> = {
  single: [
    { to: "group", label: "做成一组图", keeps: "文案会带过去;爆点需要你填 2~6 条" },
    { to: "ratioSet", label: "出全渠道套图", keeps: "文案会带过去;要勾至少 2 个渠道" },
  ],
  group: [{ to: "single", label: "改回出单张", keeps: "文案会带过去;已填的爆点会清空" }],
  ratioSet: [{ to: "single", label: "改回出单张", keeps: "文案会带过去;勾选的渠道会清空" }],
};

const PRESET_OF: Record<PosterMode, Record<string, unknown>> = {
  single: { kind: "poster_single" },
  group: { kind: "poster_group", groupPoints: [] },
  ratioSet: { kind: "poster_multiratio", ratios: ["9:16", "1:1", "16:9"] },
};

/**
 * ★2026-07-30 —— 形态条(「你正在出 商品主图」)**没有做**,原样记在这里防止有人再加一遍:
 *   页面顶部的 `PosterSourceBar` 已经在回答同一个问题(从哪张卡来 = 哪一种产出)。
 *   我先加了一条并排的形态条,结果就是同一句话在同一屏说两遍 —— 用户第一眼报的就是
 *   「有两个一样的商品主图,显示有点重复」。「我在哪一态」有一个出口就够了;
 *   要补的是 `PosterSourceBar` 在组图/套图态说清「会出几张」,不是再摆一条。
 */

/**
 * 换形态 handoff —— **在大框里**,紧跟用户写的那段话。
 * 它谈的是「我这段内容更适合做什么」,所以贴着内容而不是贴着标题栏。
 * 调用方只在写了字时渲染(没写字回卡墙即可,没有丢失风险)。
 */
export function PosterModeHandoff({
  mode,
  prompt,
  disabled,
}: {
  mode: PosterMode;
  prompt: string;
  disabled?: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const options = HANDOFF[mode];
  if (disabled) return null;

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-[12.5px] text-muted-foreground hover:text-foreground"
      >
        这段内容更适合做别的?{" "}
        <span className="font-semibold text-primary underline-offset-4 hover:underline">
          换个玩法 ▸
        </span>
      </button>

      {open && (
        <div className="space-y-1.5 rounded-lg border border-dashed border-border/70 bg-muted/20 p-2.5">
          {options.map((o) => (
            <button
              key={o.to}
              type="button"
              onClick={() =>
                // 带着文案去另一张卡 —— 走 scenePreset 那条既有 handoff 通路,
                // 不做页内 mode switch(见文件头)。
                navigate("/ai-haibao/create", {
                  state: { scenePreset: PRESET_OF[o.to], prefillPrompt: prompt },
                })
              }
              className="flex w-full items-start gap-2 rounded-md border border-border/60 bg-background/50 px-2.5 py-2 text-left transition-colors hover:border-primary/50"
            >
              <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="min-w-0">
                <span className="text-[13px] font-semibold text-foreground">{o.label}</span>
                {/* ★这一句是这个组件存在的意义:不假装无损。 */}
                <span className="ml-1.5 text-[12px] text-muted-foreground">{o.keeps}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
